<script setup lang="ts">
import { onMounted, ref, computed, onUnmounted } from 'vue'
import type { StateSalary } from '~/composables/useStateSalaryData'
import { formatCurrency } from '~/utils/formatters'

interface Props {
  mainStates: StateSalary[]
  alaskaState?: StateSalary
  hawaiiState?: StateSalary
  getStatePath: (stateCode: string) => string
  getStateColor: (salary: number | null) => string
  getStateBorder: (stateCode: string) => string
}
const props = defineProps<Props>()
const emit = defineEmits(['mouse-over', 'mouse-out', 'touch-end'])
const mapRef = ref<HTMLDivElement | null>(null)
const svgRef = ref<SVGSVGElement | null>(null)
const isMapLoaded = ref(false)
const zoomLevel = ref(1)
const minZoom = 1
const maxZoom = 3
const zoomStep = 0.2
const mapOrigin = ref({ x: 0, y: 0 })
const initialViewBox = '0 0 960 600'
const currentViewBox = ref(initialViewBox)
const isDragging = ref(false)
const dragStart = ref({ x: 0, y: 0 })
const panOffset = ref({ x: 0, y: 0 })
const baseTranslateX = -450
const baseTranslateY = -420
const mapTransform = computed(() => {
  return `scale(${1.7 * zoomLevel.value}) translate(${
    baseTranslateX + panOffset.value.x
  }, ${baseTranslateY + panOffset.value.y})`
})
const allStateCodes = [
  'al',
  'az',
  'ar',
  'ca',
  'co',
  'ct',
  'de',
  'fl',
  'ga',
  'id',
  'il',
  'in',
  'ia',
  'ks',
  'ky',
  'la',
  'me',
  'md',
  'ma',
  'mi',
  'mn',
  'ms',
  'mo',
  'mt',
  'ne',
  'nv',
  'nh',
  'nj',
  'nm',
  'ny',
  'nc',
  'nd',
  'oh',
  'ok',
  'or',
  'pa',
  'ri',
  'sc',
  'sd',
  'tn',
  'tx',
  'ut',
  'vt',
  'va',
  'wa',
  'wv',
  'wi',
  'wy',
  'dc'
]
const availableStates = computed(() => {
  return new Set(props.mainStates.map(state => state.state.toLowerCase()))
})
const continentalStates = computed(() => {
  return allStateCodes.filter(code => code !== 'ak' && code !== 'hi')
})
const filteredMainStates = computed(() => {
  return props.mainStates.filter(
    state =>
      state.state.toLowerCase() !== 'ak' && state.state.toLowerCase() !== 'hi'
  )
})
const getStateData = (stateCode: string): StateSalary | null => {
  const normalizedCode = stateCode.toLowerCase()
  return (
    props.mainStates.find(
      state => state.state.toLowerCase() === normalizedCode
    ) || null
  )
}
const getCursorPosition = (event: MouseEvent | TouchEvent) => {
  if (!svgRef.value) return { x: 0, y: 0 }
  const svg = svgRef.value
  const rect = svg.getBoundingClientRect()
  let clientX, clientY
  if ('touches' in event) {
    clientX = event.touches[0].clientX
    clientY = event.touches[0].clientY
  } else {
    clientX = event.clientX
    clientY = event.clientY
  }
  const x = clientX - rect.left
  const y = clientY - rect.top
  return { x, y }
}
const handleMouseOver = (
  event: MouseEvent | TouchEvent,
  state: StateSalary | null
) => {
  if (!state) return
  const position = getCursorPosition(event)
  emit('mouse-over', event, state, position)
}
const handleMouseOut = () => {
  emit('mouse-out')
}
const handleTouchEnd = () => {
  emit('touch-end')
}
const zoomToPoint = (delta: number, cursorX: number, cursorY: number) => {
  if (
    (delta > 0 && zoomLevel.value >= maxZoom)
    || (delta < 0 && zoomLevel.value <= minZoom)
  ) {
    return
  }
  if (!svgRef.value) return
  const svgRect = svgRef.value.getBoundingClientRect()
  const svgWidth = svgRect.width
  const svgHeight = svgRect.height
  const centerX = svgWidth / 2
  const centerY = svgHeight / 2
  const offsetX = (cursorX - centerX) / (1.7 * zoomLevel.value)
  const offsetY = (cursorY - centerY) / (1.7 * zoomLevel.value)
  const oldZoom = zoomLevel.value
  zoomLevel.value = Math.max(
    minZoom,
    Math.min(maxZoom, zoomLevel.value + delta * zoomStep)
  )
  const zoomRatio = zoomLevel.value / oldZoom
  panOffset.value.x = panOffset.value.x * zoomRatio - offsetX * (zoomRatio - 1)
  panOffset.value.y = panOffset.value.y * zoomRatio - offsetY * (zoomRatio - 1)
}
const handleWheel = (event: WheelEvent) => {
  event.preventDefault()
  const svgRect = svgRef.value?.getBoundingClientRect()
  if (!svgRect) return
  const cursorX = event.clientX - svgRect.left
  const cursorY = event.clientY - svgRect.top
  const delta = event.deltaY < 0 ? 1 : -1
  zoomToPoint(delta, cursorX, cursorY)
}
const zoomIn = () => {
  if (!svgRef.value) return
  const svgRect = svgRef.value.getBoundingClientRect()
  const centerX = svgRect.width / 2
  const centerY = svgRect.height / 2
  zoomToPoint(1, centerX, centerY)
}
const zoomOut = () => {
  if (!svgRef.value) return
  const svgRect = svgRef.value.getBoundingClientRect()
  const centerX = svgRect.width / 2
  const centerY = svgRect.height / 2
  zoomToPoint(-1, centerX, centerY)
}
const resetZoom = () => {
  zoomLevel.value = 1
}
const resetPan = () => {
  panOffset.value = { x: 0, y: 0 }
}
const resetView = () => {
  resetZoom()
  resetPan()
}
const startDrag = (event: MouseEvent | TouchEvent) => {
  // preventDefault is handled by .prevent modifier in template for touch events
  // For mouse events, we don't need preventDefault
  let clientX, clientY
  if ('touches' in event) {
    clientX = event.touches[0].clientX
    clientY = event.touches[0].clientY
  } else {
    clientX = event.clientX
    clientY = event.clientY
  }
  isDragging.value = true
  dragStart.value = { x: clientX, y: clientY }
}
const handleDrag = (event: MouseEvent | TouchEvent) => {
  if (!isDragging.value) return
  let clientX, clientY
  if ('touches' in event) {
    clientX = event.touches[0].clientX
    clientY = event.touches[0].clientY
  } else {
    clientX = event.clientX
    clientY = event.clientY
  }
  const dx = (clientX - dragStart.value.x) / (1.7 * zoomLevel.value)
  const dy = (clientY - dragStart.value.y) / (1.7 * zoomLevel.value)
  panOffset.value.x += dx
  panOffset.value.y += dy
  dragStart.value = { x: clientX, y: clientY }
}
const endDrag = () => {
  isDragging.value = false
}
onMounted(() => {
  isMapLoaded.value = true
  if (import.meta.client && svgRef.value) {
    // Add wheel listener with passive: false to allow preventDefault
    const svgElement = svgRef.value.parentElement
    if (svgElement) {
      svgElement.addEventListener('wheel', handleWheel as any, { passive: false })
    }

    window.addEventListener('mousemove', handleDrag)
    window.addEventListener('mouseup', endDrag)
    window.addEventListener('touchmove', handleDrag, { passive: false })
    window.addEventListener('touchend', endDrag)
  }
})
onUnmounted(() => {
  if (import.meta.client && svgRef.value) {
    const svgElement = svgRef.value.parentElement
    if (svgElement) {
      svgElement.removeEventListener('wheel', handleWheel as any)
    }

    window.removeEventListener('mousemove', handleDrag)
    window.removeEventListener('mouseup', endDrag)
    window.removeEventListener('touchmove', handleDrag)
    window.removeEventListener('touchend', endDrag)
  }
})
</script>

<template>
  <div
    ref="mapRef"
    class="map-container"
  >
    <!-- Zoom controls -->
      <div class="zoom-controls">
        <button
          class="zoom-btn"
          aria-label="Zoom in"
          @click="zoomIn"
        >
          +
        </button>
        <button
          class="zoom-btn"
          aria-label="Zoom out"
          @click="zoomOut"
        >
          -
        </button>
        <button
          class="zoom-btn"
          aria-label="Reset view"
          @click="resetView"
        >
          ↺
        </button>
      </div>
      <!-- Main SVG Map with zoom controls -->
      <div
        class="svg-container"
        @mousedown="startDrag"
        @touchstart.prevent="startDrag"
      >
        <svg
          ref="svgRef"
          :viewBox="currentViewBox"
          class="us-map"
          aria-label="United States map showing salary data by state"
          preserveAspectRatio="xMidYMid meet"
        >
          <!-- Continental US states - using all states with zoom and pan -->
          <g :transform="mapTransform">
            <!-- Render all continental states - colored if in data, uncolored if not -->
            <path
              v-for="stateCode in continentalStates"
              :key="stateCode"
              :d="getStatePath(stateCode)"
              :fill="
                availableStates.has(stateCode)
                  ? getStateColor(
                    getStateData(stateCode)?.median_salary || null
                  )
                  : '#ECECEC'
              "
              :stroke="getStateBorder(stateCode)"
              stroke-width="0.75"
              class="state-path"
              :class="{
                'hover:cursor-pointer': availableStates.has(stateCode)
              }"
              @mouseover="
                availableStates.has(stateCode)
                  && handleMouseOver($event, getStateData(stateCode))
              "
              @mouseout="handleMouseOut"
              @touchstart.stop="
                availableStates.has(stateCode)
                  && handleMouseOver($event, getStateData(stateCode))
              "
              @touchend.stop="handleTouchEnd"
            />
          </g>
        </svg>
      </div>
      <!-- Separate container for Alaska and Hawaii (outside the main SVG) -->
      <div class="fixed-size-labels mt-2 flex space-x-6 justify-center">
        <!-- Alaska -->
        <div
          v-if="alaskaState"
          class="state-info-item flex items-center"
          @mouseover="handleMouseOver($event, alaskaState)"
          @mouseout="handleMouseOut"
          @touchstart="handleMouseOver($event, alaskaState)"
          @touchend="handleTouchEnd"
        >
          <div
            class="color-square"
            :style="{
              backgroundColor: getStateColor(alaskaState.median_salary)
            }"
          />
          <span class="ml-2 state-fixed-label">
            Alaska: {{ formatCurrency(alaskaState.median_salary) }}
          </span>
        </div>
        <!-- Hawaii -->
        <div
          v-if="hawaiiState"
          class="state-info-item flex items-center"
          @mouseover="handleMouseOver($event, hawaiiState)"
          @mouseout="handleMouseOut"
          @touchstart="handleMouseOver($event, hawaiiState)"
          @touchend="handleTouchEnd"
        >
          <div
            class="color-square"
            :style="{
              backgroundColor: getStateColor(hawaiiState.median_salary)
            }"
          />
          <span class="ml-2 state-fixed-label">
            Hawaii: {{ formatCurrency(hawaiiState.median_salary) }}
          </span>
        </div>
      </div>
  </div>
</template>

<style scoped>
.map-container {
  max-width: 980px;
  width: 100%;
  margin: 0 auto;
  position: relative;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
}
.svg-container {
  position: relative;
  overflow: hidden;
  width: 100%;
  display: flex;
  justify-content: center;
  cursor: grab;
}
.svg-container:active {
  cursor: grabbing;
}
.us-map {
  width: 100%;
  height: auto;
  min-height: 300px; /* Prevent CLS during SVG render */
  max-height: 480px;
  aspect-ratio: 960 / 600; /* Match viewBox aspect ratio */
  display: block;
  margin: 0 auto;
}
.zoom-controls {
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 20;
  display: flex;
  flex-direction: column;
  gap: 5px;
}
.zoom-btn {
  width: 30px;
  height: 30px;
  border-radius: 4px;
  background-color: white;
  border: 1px solid #ddd;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}
.zoom-btn:hover {
  background-color: #f3f4f6;
}
.state-path {
  transition: all 0.2s ease;
  stroke-width: 0.75px;
}
.state-path:hover {
  filter: brightness(1.2);
  stroke: white !important;
  stroke-width: 2px !important;
  z-index: 10;
}
.state-path.hover\:cursor-pointer:hover {
  cursor: pointer;
}
.state-data-label {
  font-size: 12px !important;
  font-weight: 500;
  fill: #333;
  dominant-baseline: middle;
}
.state-text-display {
  cursor: pointer;
}
.state-text-display:hover rect {
  filter: brightness(1.2);
  stroke: white;
  stroke-width: 2px;
}
.state-info-box {
  background-color: rgba(255, 255, 255, 0.8);
}
.fixed-size-labels {
  padding-left: 0;
  font-size: 12px;
  margin-top: 5px;
  width: 100%;
  display: flex;
  justify-content: center;
}
.state-fixed-label {
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
}
.color-square {
  width: 16px;
  height: 16px;
  border: 1px solid #666;
}
.state-info-item {
  cursor: pointer;
}
.state-info-item:hover .color-square {
  filter: brightness(1.2);
  border: 2px solid white;
  box-shadow: 0 0 0 1px #666;
}
.map-status {
  position: absolute;
  bottom: 10px;
  right: 10px;
  background-color: rgba(255, 255, 255, 0.8);
  padding: 5px 10px;
  border-radius: 4px;
  font-size: 12px;
  z-index: 20;
}
.reset-btn {
  background-color: #4f46e5;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 4px 8px;
  font-size: 12px;
  cursor: pointer;
}
.reset-btn:hover {
  background-color: #4338ca;
}
</style>
